/*
Various accents of English.
Uses lexical sets https://en.wikipedia.org/wiki/Lexical_set
Adapted from https://en.wikipedia.org/wiki/International_Phonetic_Alphabet_chart_for_English_dialects
  some sounds have multiple options, so by picking just one of each, it doesn't necessarily match an actual accent,
  I typically picked one that was further from American English,
  and preferred <3 vowels because that's easier on the speech synthesizer
*/

import { englishPhonotactics } from "../phonotactics/englishPhonotactics";
import { AddLengthRule } from "../rules/lengthRules";
import { Rule } from "../rules/rule";
import { RuleSet } from "../rules/ruleSet";

/** common aspiration rules - at start of word or stressed syllable & before a vowelish phoneme, aspirate */
const aspFollowing = { or: [':vowel', ':approximant', ':lateral-approximant', ':syllabic'] };
export const aspirationRules: Rule[] = [
  { from: 'p', to: 'pʰ', previous: ['#word'], next: aspFollowing },
  { from: 'p', to: 'pʰ', previous: { not: '/s/' }, next: aspFollowing, syllable: { current: 'stressed' } },
  { from: 't', to: 'tʰ', previous: ['#word'], next: aspFollowing },
  { from: 't', to: 'tʰ', previous: { not: '/s/' }, next: aspFollowing, syllable: { current: 'stressed' } },
  { from: 'k', to: 'kʰ', previous: ['#word'], next: aspFollowing },
  { from: 'k', to: 'kʰ', previous: { not: '/s/' }, next: aspFollowing, syllable: { current: 'stressed' } },
  // any t's that are left are strong enough to be aspirated
  { from: 't', to: 'tʰ', previous: { not: '/s/' }, next: aspFollowing },  // e.g., overton, chieftain, returnee

  { from: 'hw', to: 'w' },
];

/** handle dark l */
const lRules: Rule[] = [
  { from: "əˈɫ", to: "əˈɫ" }, { from: "əˌɫ", to: "əˌɫ" },
  { from: 'əɫ', to: 'ɫ̩', next: { or: ['#word', ':consonant'] } },
];

/**
 * a non-rhotic language should have a trailing /ɹ/ or /ɾ/ in the rhotic lexical sets (like start),
 * then add 'nonRhoticRules' to their rules to strip it off when not followed by a vowel
 */
export const nonRhoticRules: Rule[] = [
  { from: 'ɹ', to: '', previous: ['vowel'], next: { not: ':vowel' } },
  { from: 'ɾ', to: '', previous: ['vowel'], next: { not: ':vowel' } },
  { from: 'ɝ', to: 'ɜ', next: { not: ':vowel' } },
  { from: 'ɚ', to: 'ə', next: { not: ':vowel' } },
];

/** common rules across accents, which run after everything else */
export const commonFinalRules: Rule[] = [...aspirationRules, ...lRules];

/** general rules for adjusting phoneme lengths */
const englishLengthen: AddLengthRule[] = [
  /** shorten vowels before a consonant cluster (CCC, non-syllabic) */
  {
    addMs: 0, // but don't shorten if current & next are both stressed
    next: { seq: [':consonant', { and: [':consonant', { not: ':syllabic' }] }, { and: [':consonant', { not: ':syllabic' }] }] },
    syllable: { current: 'stressed', next: ['stressed'] },
  },
  {
    addMs: -50,
    next: { seq: ['/m/', '/p/', { or: ['/ɫ/', '/ʃ/', '/t/', '/tʰ/'] }] }
  },
];


//
// dictionaries
export const dictionaryUS: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  lexicalSets: {
    FACE: 'eɪ̯', PRICE: 'aɪ̯', GOAT: 'oʊ̯', CHOICE: 'ɔɪ̯', MOUTH: 'aʊ̯',
    START: 'ɑɹ', SQUARE: 'ɛɹ', lettER: 'ɝ', NEAR: 'ɪɹ', NORTH: 'ɔɹ', FORCE: 'ɔɹ', CURE: 'ʊɹ', NURSE: 'ɝ',
    PALM: 'ɑ', LOT: 'ɑ', THOUGHT: 'ɔ', FLEECE: 'i', GOOSE: 'u', TRAP: 'æ', BATH: 'æ',
    CLOTH: 'ɔ', commA: 'ə', DRESS: 'ɛ', happY: 'i', KIT: 'ɪ', STRUT: 'ə', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      eɪ̯: 230, aɪ̯: 250, oʊ̯: 230, ɔɪ̯: 285, aʊ̯: 235,
      ɑɹ: 260, ɛɹ: 280, ɪɹ: 280, ɔɹ: 250, ʊɹ: 250,
      ɝ: 210, ɑ: 210, ɔ: 200, æ: 210, ɛ: 195,
      i: 170, ɪ: 160, u: 190, ʊ: 145, ə: 130,
    },
    adds: englishLengthen,
  },
};

export const dictionaryUK: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  lexicalSets: {
    FACE: 'eɪ̯', PRICE: 'aɪ̯', GOAT: 'əʊ̯', CHOICE: 'ɔɪ̯', MOUTH: 'aʊ̯',
    START: 'ɑːɹ', SQUARE: 'eə̯ɹ', lettER: 'ɐɹ', NEAR: 'iə̯ɹ', NORTH: 'ɔːɹ', FORCE: 'ɔːɹ', CURE: 'ɔːɹ', NURSE: 'ɜːɹ',
    PALM: 'ɑː', LOT: 'ɒ', THOUGHT: 'ɔː', FLEECE: 'iː', GOOSE: 'uː', TRAP: 'æ', BATH: 'ɑː',
    CLOTH: 'ɒ', commA: 'ɐ', DRESS: 'ɛ', happY: 'i', KIT: 'ɪ', STRUT: 'ʌ', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      eɪ̯: 220, aɪ̯: 230, əʊ̯: 220, ɔɪ̯: 230, aʊ̯: 230, eə̯: 220, iə̯: 240,
      ɑː: 200, ɔː: 230, ɜː: 210, iː: 175, uː: 190,
      ɐ: 150, ɒ: 150, æ: 175, ɛ: 140, i: 140, ɪ: 130, ʊ: 150, ʌ: 140,
    },
    adds: englishLengthen,
  },
  rules: nonRhoticRules,
};

export const westernUS: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  wholeWords: {
    your: 'jɝ', yours: 'jɝz',
    interfere: 'ˌɪnɝˈfɪɹ',  // in NEAR, but is fairly different from RP
    insult: 'ˈɪnsɫ̩t',
    bahamas: 'bɐˈhɑmɐz',
    // words in CLOTH (or START) pronounced with /oɹ/ rather than /ɑɹ/
    orange: 'ˈoɹənd͡ʒ', moral: 'ˈmoɹəɫ', warrant: 'ˈwoɹənt', historic: 'hɪˈstoɹɪk', prehistoric: 'ˌpɹihɪˈstoɹɪk', porridge: 'ˈpoɹəd͡ʒ',
    categorical: 'ˌkɛə̯təˈɡoɹəkəɫ', florida: 'ˈfɫoɹədə', florist: 'ˈfɫoɹəst', laurence: 'ˈɫoɹəns', lawrence: 'ˈɫoɹəns',
    laurel: 'ˈɫoɹəɫ', laureate: 'ˈɫoɹiət', moribund: 'ˈmoɹəbənd',
    forage: "ˈfoɹɪd͡ʒ", forages: "ˈfoɹɪd͡ʒəz", foraging: "ˈfoɹɪd͡ʒɪŋ",
    foreign: "ˈfoɹən", foreigner: "ˈfoɹənɝ", foreigners: "ˈfoɹənɝz", "foreigners'": "ˈfoɹənɝz",
    forest: "ˈfoɹəst", "forest's": "ˈfoɹəsts", forested: "ˈfoɹəstəd", forester: "ˈfoɹəstɝ",
    foresters: "ˈfoɹəstɝz", forestry: "ˈfoɹəstɹi", forests: "ˈfoɹəsts", forestville: "ˈfoɹɛstˌvɪɫ",
    origami: 'ˌoɹɐˈɡɑˌmi',
    // words in CURE that are pronounced /oɹ/ rather than /ʊɹ/
    poor: 'ˈpoɹ', spoor: 'ˈspoɹ',
    // words in BATH that are pronounced /ɑ/
    plaza: 'ˈpɫɑzə', plazas: 'ˈpɫɑzəz', "plaza's": 'ˈpɫɑzəz',
    // compound words that flap between vowels at word boundary
    cutout: 'ˈkəˌᴅaʊ̯t', shootout: 'ˈʃuᴅaʊ̯t', shutout: 'ˈʃəˌᴅaʊ̯t',
    whatever: 'ˌwəˈᴅɛvɝ', writeoff: 'ˈɹaɪ̯ᴅɑf',
    // intersection of multiple rules
    startup: 'ˈstɑɹᴅɐp', startups: 'ˈstɑɹᴅɐps',
  },
  syllableBreaks: englishPhonotactics,
  lexicalSets: {
    FACE: 'e̞i̯', PRICE: 'aɪ̯', GOAT: 'ɤ̞o̯', CHOICE: 'ɔɪ̯', MOUTH: 'aʊ̯',
    START: 'ɑɹ', SQUARE: 'e̞ɹ', NEAR: 'ɪɹ', NORTH: 'oɹ', FORCE: 'oɹ',
    CURE: 'ɤɹ', lettER: 'ɝ', NURSE: 'ɝ',
    PALM: 'ɑ', LOT: 'ɑ', THOUGHT: 'ɑ', CLOTH: 'ɑ', TRAP: 'ɛə̯', BATH: 'ɛə̯',
    DRESS: 'ɛ', FLEECE: 'i', happY: 'i', KIT: 'ɪ', GOOSE: 'ɯu̯', FOOT: 'ʊ̞',
    STRUT: 'ə', commA: 'ə',
  },
  phonemeLengths: {
    map: {
      ɑ: 200, ɛə̯: 160, ɛ: 195, i: 170, ɪ: 160, ɤ̞o̯: 230, əː: 220,
      ʊ̞: 145, ə: 130, ɯu̯: 190, ɐ: 140, ɨ: 110,
      aɪ̯: 250, aʊ̯: 235, e̞i̯: 230, ɔɪ̯: 285,
      ɑɹ: 260, e̞ɹ: 280, ɪɹ: 280, oɹ: 250, ɤɹ: 280, ɝ: 210,
      o: 230, əl: 130, əɫ: 130, ɫ̩: 130,
    },
    adds: englishLengthen,
  },
  rules: [
    // words that don't flap in "foot-initial" syllables
    // (put in rules so that it doesn't add a flap)
    { from: 'ˌmɛ.də.tɝ.ˈe̞i̯.ni.ən', to: 'ˌmɛ.dɨ.tɝ.ˈe̞i̯.ni.n̩' }, // mediterranean
    { from: 'ˌmɪ.ɫɪ.tɝ.ˈɪ.stɪk', to: 'ˌmɪ.ɫɪ.tɝ.ˈɪ.stɪk' }, // militaristic
    { from: 'ɹɪ.tɝ.ˈni', to: 'ɹɪ.tɝ.ˈni' }, // returnee

    // before /l/, both /o/ & /u/ stay as back vowels rather than [ɤ̞o̯] & [ɯu̯]
    { from: 'ɤ̞o̯', to: 'o', next: { or: ['/ɫ/', '/l/'] } },
    { from: 'ɯu̯', to: 'u', next: { or: ['/ɫ/', '/l/'] } },
    // espeak's /uw/ is close to [ɯu̯], which generally sounds reasonable in GA,
    // hence GOOSE>ˈɡɯu̯s, but other uses should also be converted
    { from: 'ɯu̯', to: 'ɯu̯' }, // mark as replaced so later rules ignore it
    { from: 'u', to: 'ɯu̯' },
    // similar for /æ/ > [ɛə̯]
    { from: 'ɛə̯', to: 'ɛə̯' }, // mark as replaced so later rules ignore it
    { from: 'æ', to: 'ɛə̯' },
    // convert leftover diphthongs
    { from: 'oʊ̯', to: 'ɤ̞o̯' },
    // /ʊ/ > [ʊ̞], but preserve other uses of /ʊ/
    { from: 'ʊ.ˈɹ', to: 'ʊ.ˈɹ' }, { from: 'ʊ.ˌɹ', to: 'ʊ.ˌɹ' },
    { from: 'ʊˈɹ', to: 'ʊˈɹ' }, { from: 'ʊˌɹ', to: 'ʊˌɹ' },
    { from: 'ʊ.ɹ', to: 'ʊ.ɹ' }, { from: 'ʊɹ', to: 'ʊɹ' },
    { from: 'ʊ̯', to: 'ʊ̯' }, { from: 'ɤ̞o̯', to: 'ɤ̞o̯' },
    { from: 'ʊ', to: 'ʊ̞' },

    // əl > ɫ̩
    { from: 'ə.ˈɫ', to: 'ɐ.ˈɫ', previous: ['#word'] }, // mark as replaced so later rules ignore it
    { from: 'kə.ˈɫ', to: 'kɐ.ˈɫ', previous: ['#word'] },  // e.g. <collect>
    { from: 'ə.ˈɫ', to: 'ɐ.ˈɫ', syllable: { prev: ['stressed'], current: 'unstressed', next: ['stressed'] } },
    { from: 'ə.ˌɫ', to: 'ɐ.ˌɫ', syllable: { prev: ['stressed'], current: 'unstressed', next: ['stressed'] } },
    { from: 'ə.ˈɫ', to: 'ɨ.ˈɫ' }, { from: 'ə.ˌɫ', to: 'ɨ.ˌɫ' },
    { from: 'ə.ɫ', to: 'ɨ.ɫ', next: ['vowel'], syllable: { current: 'unstressed', next: ['unstressed'] } },
    { from: 'əɫ', to: 'ɫ̩', syllable: { current: 'unstressed' } },

    // əm > m̩
    { from: 'ə.ˈm', to: 'ɐ.ˈm', previous: ['#word'] }, // mark as replaced so later rules ignore it
    { from: 'ə.ˈm', to: 'ɨ.ˈm' }, { from: 'ə.ˌm', to: 'ɨ.ˌm' },
    { from: 'əm', to: 'm̩', syllable: { current: 'unstressed' } },

    // ən > n̩
    { from: 'ə.ˈn', to: 'ɐ.ˈn', previous: ['#word'] }, // mark as replaced so later rules ignore it, e.g., kenobi /kə.ˈnʌo̯.ˌbi/
    { from: 'ə.ˌn', to: 'ɐ.ˌn', previous: ['#word'] },
    { from: 'ə.ˈn', to: 'ɨ.ˈn' }, { from: 'ə.ˌn', to: 'ɨ.ˌn' },
    { from: 'ən', to: 'ɐn', previous: ['#word'] },
    { from: 'tən', to: 'tʰn̩', syllable: { current: 'unstressed', prev: ['unstressed', 'stressed'] } }, // e.g., overton /ˈʌo̯.vɝ.tʰn̩/
    { from: 'ntən', to: '.ʔn̩', syllable: { current: 'stressed', next: ['unstressed'] } }, // e.g. mountain /ˈmaʊ̯.ʔn̩/
    { from: 'tən', to: 'ʔn̩', syllable: { current: 'unstressed' }, previous: { or: [":vowel", "/ɹ/"] } }, // e.g. button /ˈbɐ.ʔn̩/
    { from: 'ən', to: 'n̩', syllable: { current: 'unstressed' } }, // e.g., chieftain /ˈt͡ʃif.tn̩/, distance /ˈdɪ.stn̩s/
    { from: 'tnɪŋ', to: 'ʔ.nɪŋ', syllable: { current: 'stressed', next: ['unstressed'] }, previous: { or: [":vowel", "/ɹ/"] } }, // e.g. lightning /ˈɫaɪ̯ʔ.nɪŋ/

    // t > ᴅ
    {
      from: 't', to: 'ᴅ', syllable: { current: 'unstressed' },
      previous: ["vowel"],
      next: { or: ["/ə/", "/ɐ/", "/ɨ/", "/ɪ/", "/i/", "/ɤ̞o̯/", ":rhotacized", ":syllabic"] }
    },
    {
      from: 't', to: 'ᴅ', syllable: { current: 'unstressed' },
      previous: { or: [":rhotacized", "/ɹ/", "/n̩/"] },
      next: { or: [":vowel", ":rhotacized", ":syllabic"] }, // e.g., liberty, party, smarter, artemis
    },
    { from: 'aɪ̯n.ˈti', to: 'aɪ̯n.ˈti' }, // 19 doesn't change
    { from: 'aɪ̯n.ti', to: 'aɪ̯n.ᴅi' }, // but 90 does

    // change /ə/
    { from: 'ðə', to: 'ðɨ', previous: ['#word'], next: ['#word'] },  // <the>
    { from: 'ə', to: 'ɐ', previous: ['#word'] },
    { from: 'ə', to: 'ɐ', next: ['#word'] },
    { from: 'ə', to: 'ɐ', syllable: { current: 'stressed' } },
    { from: 'ə', to: 'ɐ', syllable: { prev: ['stressed'], current: 'unstressed', next: ['stressed'] } },
    { from: 'ə', to: 'ɨ' }, // unstressed
  ],
  rules2: commonFinalRules,
};

// contemporary
export const receivedPronunciation: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    interfere: 'ˌɪntəfˈɪə̯',  // in NEAR, but is fairly different from GA
  },
  lexicalSets: {
    FACE: 'e̞ɪ̯', PRICE: 'aɪ̯', GOAT: 'əʊ̯', CHOICE: 'ɔɪ̯', MOUTH: 'aʊ̯',
    START: 'ɑːɹ', SQUARE: 'ɛə̯ɹ', NEAR: 'ɪə̯ɹ', NORTH: 'ɔːɹ', FORCE: 'ɔːɹ', CURE: 'ɤ̈ːɹ', lettER: 'əɹ', NURSE: 'əːɹ',
    PALM: 'ɑː', LOT: 'ɔ', THOUGHT: 'o̞ː', CLOTH: 'ɔ', TRAP: 'a', BATH: 'ɑː', DRESS: 'ɛ̝', FLEECE: 'iː',
    happY: 'i', KIT: 'ɪ', GOOSE: 'uː', FOOT: 'ɤ̈', STRUT: 'ɐ', commA: 'ə'
  },
  phonemeLengths: {
    map: {
      ɑː: 200, ɔː: 230, ɔ: 150, a: 175, ɛ̝: 140, iː: 175, ɪ: 130,
      o̞ː: 230, uː: 190, ɤ̈: 150, ə: 150, əː: 210,
      aɪ̯: 230, aʊ̯: 230, e̞ɪ̯: 220, ɛə̯: 220, ɪə̯: 240, ɔɪ̯: 230,
      ɤ̈ː: 230, əʊ̯: 220, əl: 130, əɫ: 130, ɫ̩: 130,
      ɐ: 140, i: 140,
    },
    adds: englishLengthen,
  },
  rules: [
    ...nonRhoticRules
  ],
  rules2: commonFinalRules,
};

// https://en.wikipedia.org/wiki/Cockney
export const cockney: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    'my': 'mi', 'myself': 'misˈɛɫf',
  },
  lexicalSets: {
    FACE: 'æɪ̯', PRICE: 'ɒɪ̯', GOAT: 'ɐʊ̯', CHOICE: 'oɪ̯', MOUTH: 'æː',
    START: 'ɑːɹ', SQUARE: 'ɛːɹ', NEAR: 'ɪːɹ', NORTH: 'oːɹ', FORCE: 'oːɹ', CURE: 'ɔːɹ', lettER: 'ɐɹ', NURSE: 'ɜːɹ',
    PALM: 'ɑː', LOT: 'ɔ', THOUGHT: 'ɔ̝ə̯', CLOTH: 'ɒ', TRAP: 'ɛ', BATH: 'ɑː', DRESS: 'eə̯', FLEECE: 'əi̯',
    happY: 'i', KIT: 'ɪ', GOOSE: 'ʉː', FOOT: 'ʊ', STRUT: 'a', commA: 'ɐ'
  },
  phonemeLengths: {
    map: {
      ɑː: 200, æː: 230, ɛː: 220, ɪː: 240, oː: 230, ɔː: 230, ʉː: 190, ɜː: 210,
      a: 140, ɐ: 150, ɒ: 150, ɛ: 175, i: 140, ɪ: 130, ɔ: 150, ʊ: 150, ə: 130,
      ɒɪ̯: 230, ɐʊ̯: 220, æɪ̯: 220, eə̯: 190, oɪ̯: 230, ɔ̝ə̯: 230, əi̯: 190,
      əl: 130, əɫ: 130, ɫ̩: 130,
    },
    adds: englishLengthen,
  },
  rules: [
    { from: 'tj', to: 't͡ʃ' }, // t͡ʃʰ
    { from: 'dj', to: 'd͡ʒ' },

    // t > ʔ and ŋ > n
    { from: 'tnɪŋ', to: 'ʔ.nɪn', syllable: { current: 'stressed', next: ['unstressed'] } }, // e.g. lightning /ɫˈɒɪ̯ʔ.nɪn/
    { from: 'ntɪn', to: '.ʔɪn', syllable: { current: 'stressed', next: ['unstressed'] } }, // e.g. mountain /mˈæː.ʔɪn/

    { from: 'hw', to: 'hw', previous: ['#word'] }, // so we skip the next rule
    { from: 'h', to: 'ʔ', previous: ['#word'] },
    { from: 'ɫ', to: 'w', previous: ['vowel'], next: ['#word'] },
    { from: 'ŋ', to: 'n', next: ['#word'] },
    { from: 't', to: 'ʔ', previous: ['vowel'], next: ['#word'], syllable: { current: 'unstressed' } },
    { from: 't', to: 'ʔ', previous: { or: [':vowel', '/ɹ/'] }, next: ['vowel'], syllable: { current: 'unstressed' } },
    { from: 'ð', to: 'd', previous: ['#word'] },
    { from: 'ð', to: 'v' },
    { from: 'θ', to: 'f' },

    ...nonRhoticRules
  ],
  rules2: commonFinalRules,
};

// https://en.wikipedia.org/wiki/Scouse
export const liverpool: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    'my': 'mi',
    'myself': 'misˈɛlf',
    'school': 'ˈskɨ̞u̯l', 'schools': 'ˈskɨ̞u̯lz',
    'ding': 'ˈdɪŋg', // not the <ing> rule
    'mom': 'ˈmʊm',
    // debuccalisation - sample words
    // it lot not that at what aggregate maggot market
    // ɪt lˈɑːt nˌɑːt ðæt æt wˌʌt ˈæɡɹɪɡˌeɪ̯t mˈæɡət mˈɑːɹkɪt
    // ɪh lɒh nɒh d̪ah ah wɒh ˈaɡɾɪɡɪh ˈmaɡɪh ˈmɑːxɪh
    'it': 'ɪh',
    'lot': 'ˈlɒh',
    'not': 'nɒh',
    'that': 'd̪ah',
    'at': 'ah',
    'what': 'wɒh',
    'aggregate': 'ˈaɡɾɪɡɪh',
    'maggot': 'ˈmaɡɪh',
    'market': 'ˈmɑːxɪh',
    // words that change like GOOSE rather than FOOT
    'book': 'ˈbʉːx ',
    'cook': 'ˈkʉːx',
    'look': 'ˈlʉːx',
    // unstressed /ɪ/ can change to /ə/
    'eleven': 'əlˈɛvən', 'elevens': 'əlˈɛvənz',
    'orange': 'ˈɒɹənd͡ʒ', 'oranges': 'ˈɒɹənd͡ʒəz',
  },

  lexicalSets: {
    FACE: 'eɪ̯', PRICE: 'aɪ̯', GOAT: 'ɛʉ̯', CHOICE: 'ɔɪ̯', MOUTH: 'aʊ̯',
    START: 'aːɾ', SQUARE: 'eːɾ', lettER: 'əɾ', NEAR: 'iɛ̯ɾ', NORTH: 'o̞ːɾ', FORCE: 'o̞ːɾ', CURE: 'uɛ̯ɾ', NURSE: 'eːɾ',
    PALM: 'aː', LOT: 'ɒ', THOUGHT: 'o̞ː', FLEECE: 'iː', GOOSE: 'ʉː', TRAP: 'a', BATH: 'a',
    CLOTH: 'ɒ', commA: 'ə', DRESS: 'ɛ', happY: 'i', KIT: 'ɪ', STRUT: 'ʊ', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      aː: 220, iː: 190, o̞ː: 230, ʉː: 190, əː: 220,
      a: 175, ɒ: 150, ɛ: 140, i: 140, ɪ: 130, ʊ: 150, ə: 150,
      aɪ̯: 230, aʊ̯: 230, eɪ̯: 220, ɛʉ̯: 250, ɔɪ̯: 230,
      eː: 220, iɛ̯: 240, uɛ̯: 230, əl: 130, əɫ: 130, ɫ̩: 130,
      ɐ: 140,
    },
    adds: englishLengthen,
  },

  rules: [
    ...nonRhoticRules,
    { from: 'h', to: '', previous: ['#word'] },
    { from: 'θ', to: 't̪' },
    { from: 'ð', to: 'd̪' },
    { from: 'ɫ', to: 'l' },
    { from: 'k', to: 'x', previous: ['vowel'], next: { or: ['#word', ':vowel'] } },
    { from: 't', to: 'ᴅ', previous: ['vowel'], next: ['vowel'], syllable: { current: 'unstressed' } },
    { from: 'nɪŋ', to: 'nən', next: ['#word'], syllable: { current: 'unstressed' } },
    { from: 'ɪŋ', to: 'ʔn̩', next: ['#word'], syllable: { current: 'unstressed' } },
    { from: 'ŋ', to: 'ŋg', next: ['#word'] },
  ],
  rules2: commonFinalRules,
};

// Brummie (Birmingham) - https://en.wikipedia.org/wiki/Brummie_dialect
export const brummie: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    "her": "ɜː",
    // some SQUARE words behave like NEAR
    "there": "ˈðɪə̯", "where": "ˈwɪə̯",
  },
  lexicalSets: {
    FACE: 'aɪ̯', PRICE: 'ɒɪ̯', GOAT: 'aʊ̯', CHOICE: 'ɒɪ̯', MOUTH: 'æə̯',
    START: 'ɑːɹ', SQUARE: 'ɛə̯ɹ', NEAR: 'iə̯ɹ', NORTH: 'o̞ːɹ', FORCE: 'o̞ːɹ',
    CURE: 'ɔːɹ', lettER: 'ɐɹ', NURSE: 'ɘ̝ːɹ',
    PALM: 'ɑː', LOT: 'ɒ', THOUGHT: 'o̞ː', CLOTH: 'ɒ', TRAP: 'a', BATH: 'a',
    DRESS: 'ɛ', FLEECE: 'əi̯', happY: 'əi̯', KIT: 'ɪ', GOOSE: 'əʉ̯', FOOT: 'ɤ',
    STRUT: 'ʌ', commA: 'ɐ',
  },
  phonemeLengths: {
    map: {
      ɑː: 220, ɘ̝ː: 210, o̞ː: 230, ɔː: 230, əː: 220,
      a: 170, ɒ: 160, ɐ: 150, ɛ: 140, ɪ: 130, ɤ: 150, ʌ: 140, ə: 130,
      aɪ̯: 220, ɒɪ̯: 230, aʊ̯: 220, æə̯: 230,
      ɛə̯: 220, iə̯: 240, əi̯: 190, əʉ̯: 230, əl: 130, əɫ: 130, ɫ̩: 130,
      i: 140,
    },
    adds: englishLengthen,
  },
  rules: [
    { from: 'h', to: '', previous: ['#word'] },   // h > ∅ / #_
    { from: 'ɹ', to: 'ɾ', next: ['vowel'] },      // ɹ > ɾ / _V
    { from: 'ŋ', to: 'ŋg', next: { or: ['#word', ':vowel'] } }, // ŋ > ŋg / _{#|V}
    { from: 't', to: 't̪', next: ['#word'] },      // t > t̪ / _# (not the right [t])
    ...nonRhoticRules,
  ],
  rules2: commonFinalRules,
};

// estuary english - https://en.wikipedia.org/wiki/Estuary_English
export const estuary: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  lexicalSets: {
    FACE: 'ɛɪ̯', PRICE: 'ɑɪ̯', GOAT: 'ɐʊ̯', CHOICE: 'ɔɪ̯', MOUTH: 'æʊ̯',
    START: 'ɑːɹ', SQUARE: 'ɛ̝ːɹ', NEAR: 'ɪːɹ', NORTH: 'o̟ːɹ', FORCE: 'o̟ːɹ',
    CURE: 'ʉ̜ə̯ɹ', lettER: 'əɹ', NURSE: 'ɜːɹ',
    PALM: 'ɑ', LOT: 'ɒ', THOUGHT: 'ɔə̯', CLOTH: 'ɒ', TRAP: 'æ', BATH: 'ɑː',
    DRESS: 'ɛ', FLEECE: 'ɪi̯', happY: 'ɪi̯', KIT: 'ɪ', GOOSE: 'ʉː', FOOT: 'ʊ',
    STRUT: 'ɐ', commA: 'ə',
  },
  phonemeLengths: {
    map: {
      ɛɪ̯: 220, ɑɪ̯: 230, ɐʊ̯: 220, ɔɪ̯: 230, æʊ̯: 230, ɛ̝ː: 220, ɜː: 210, əː: 220,
      ɪː: 220, o̟ː: 230, ʉ̜ə̯: 230, ɝ: 190, ɒ: 150, ɑː: 220, ɔə̯: 230, æ: 175,
      ɑ: 190, ɛ: 140, ɪi̯: 190, ɪ: 130, ʉː: 200, ʊ: 150, ɐ: 140, ə: 150,
      əl: 130, əɫ: 130, ɫ̩: 130,
    },
    adds: englishLengthen,
  },
  rules: [
    // l-vocalization
    { from: '.əɫ', to: 'ɫ', previous: 'æʊ̯' },   // əɫ > ɫ / æʊ̯_
    { from: 'ɫ', to: '', previous: { or: ['/o̟ː/', '/ɔə̯/', '/ʊ/', '/ʊ̯/'] } },   // ɫ > ∅ / {o̟ː|ɔə̯|ʊ|ʊ̯}_
    /*
    // pre-glottalization
    { from: 'p', to: 'ʔp', previous: ['vowel'], next: { or: [':consonant', '#word'] } },   // p > ʔp / V_{C|#}
    { from: 'k', to: 'ʔk', previous: ['vowel'], next: { or: [':consonant', '#word'] } },   // k > ʔk / V_{C|#}
    { from: 't͡ʃ', to: 'ʔt͡ʃ', previous: ['vowel'] },   // t͡ʃ > ʔt͡ʃ / V_
    */
    // t-glottalization
    { from: 't', to: 'ʔ', previous: { or: [':vowel', '/n/'] }, next: ['consonant'] },   // t > ʔ / {V|n}_C
    { from: 't', to: 'ʔ', next: ['#word'] },   // t > ʔ / _#
    { from: 't', to: 'ʔ', previous: ['vowel'], next: ['vowel'], syllable: { prev: ['stressed'], current: 'unstressed' } },   // t > ʔ / V_V 'S_U_'
    // yod-coalescence
    { from: 'dj', to: 'd͡ʒ' },
    { from: 'tj', to: 't͡ʃ' },
    // th-fronting
    { from: 'ð', to: 'v' },
    { from: 'θ', to: 'f' },
    // non-rhotic
    ...nonRhoticRules,
  ],
  rules2: commonFinalRules,
};

export const newDublin: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  lexicalSets: {
    FACE: 'eː', PRICE: 'ɑɪ̯', GOAT: 'əʊ̯', CHOICE: 'ɒɪ̯', MOUTH: 'ɛʊ̯',
    START: 'äːɹ', SQUARE: 'eːɹ', lettER: 'ɚ', NEAR: 'iːɹ', NORTH: 'ɒːɹ', FORCE: 'ɔɹ', CURE: 'uːɹ', NURSE: 'ɚː',
    PALM: 'aː', LOT: 'ɑ', THOUGHT: 'ɒː', FLEECE: 'iː', GOOSE: 'uː', TRAP: 'a', BATH: 'aː',
    CLOTH: 'ɔː', commA: 'ə', DRESS: 'ɛ', happY: 'iː', KIT: 'ɪ', STRUT: 'ɤ', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      eː: 220, ɑɪ̯: 230, əʊ̯: 220, ɒɪ̯: 230, ɛʊ̯: 230,
      äːɹ: 250, eːɹ: 250, iːɹ: 250, ɒːɹ: 250, ɔɹ: 220, uːɹ: 250, ɚ: 190, ɚː: 230,
      aː: 210, ɑ: 150, ɒː: 230, iː: 190, uː: 210, a: 175, əː: 220, ɔː: 210, ə: 150,
      ɛ: 140, ɪ: 130, ɤ: 140, ʊ: 150, əl: 130, əɫ: 130, ɫ̩: 130,
      ɐ: 140, i: 140,
    },
    adds: englishLengthen,
  },
  rules: commonFinalRules,
};

export const localDublin: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  lexicalSets: {
    FACE: 'eː', PRICE: 'əɪ̯', GOAT: 'ʌu̯', CHOICE: 'aɪ̯', MOUTH: 'eʊ̯',
    START: 'æːɹ', SQUARE: 'eːɹ', lettER: 'ɚ', NEAR: 'iːɹ', NORTH: 'äːɹ', FORCE: 'ɒːɹ', CURE: 'uːɹ', NURSE: 'ʊːɹ',
    PALM: 'æː', LOT: 'ä', THOUGHT: 'aː', FLEECE: 'iː', GOOSE: 'uː', TRAP: 'æ', BATH: 'æ',
    CLOTH: 'ä', commA: 'ə', DRESS: 'ɛ', happY: 'iː', KIT: 'ɪ', STRUT: 'ʊ', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      eː: 220, əɪ̯: 230, ʌu̯: 220, aɪ̯: 230, eʊ̯: 230,
      ɛwə̆: 230, əjə̆: 230, uwə̆: 230, ijə̆: 230,
      æːɹ: 250, eːɹ: 250, iːɹ: 250, äːɹ: 250, ɒːɹ: 250, uːɹ: 250, ʊːɹ: 250, ɚ: 190,
      æː: 220, ä: 150, aː: 230, iː: 200, uː: 220, æ: 160, əː: 220,
      ə: 150, ɛ: 140, ɪ: 130, ʊ: 150,
      ɐ: 140, i: 140,
    },
    adds: englishLengthen,
  },
  rules: [
    /* the speech synthesizer isn't great with these, so leaving them off for now
    // vowel breaking in closed syllables, i.e., ending with C
    { from: 'eʊ̯', to: 'eʊ̯', next: '.' }, { from: 'eʊ̯', to: 'ɛwə̆', next: ['consonant'] },  // MOUTH
    { from: 'əɪ̯', to: 'əɪ̯', next: '.' }, { from: 'əɪ̯', to: 'əjə̆', next: ['consonant'] },  // PRICE
    { from: 'uː', to: 'uː', next: '.' }, { from: 'uː', to: 'uwə̆', next: ['consonant'] },  // GOOSE
    { from: 'iː', to: 'iː', next: '.' }, { from: 'iː', to: 'ijə̆', next: ['consonant'] },  // FLEECE
    */

    { from: 'θ', to: 't' },
    { from: 'ð', to: 'd' },
    { from: 't', to: 'h', previous: { and: [':vowel', { not: ':long' }] }, next: ['#word'], syllable: { current: 'unstressed' } },
    { from: 't', to: 'h', previous: ['vowel'], next: ['vowel'], syllable: { current: 'unstressed' } },
  ],
  rules2: commonFinalRules,
};

export const welshCardiff: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  lexicalSets: {
    FACE: 'ei̯', PRICE: 'ɜi̯', GOAT: 'ɘu̯', CHOICE: 'ʌ̈i̯', MOUTH: 'ɑ̟u̯',
    START: 'aːɹ', SQUARE: 'ɛːɹ', lettER: 'əɹ', NEAR: 'iːə̯ɹ', NORTH: 'ʌ̈ːɹ', FORCE: 'ʌ̈ːɹ', CURE: 'ʌ̈ːɹ', NURSE: 'øːɹ',
    PALM: 'aː', LOT: 'ɑ̟', THOUGHT: 'ʌ̈ː', FLEECE: 'iː', GOOSE: 'uː', TRAP: 'aː', BATH: 'a',
    CLOTH: 'ɑ̟', commA: 'ə', DRESS: 'ɛ', happY: 'iː', KIT: 'ɪ̞', STRUT: 'ə', FOOT: 'ɘ',
  },
  phonemeLengths: {
    map: {
      ei̯: 220, ɜi̯: 230, ɘu̯: 220, ʌ̈i̯: 230, ɑ̟u̯: 230,
      aː: 240, ɛː: 220, iːə̯: 270, øː: 220,
      ɑ̟: 150, ʌ̈ː: 230, iː: 200, uː: 220, əː: 220,
      a: 160, ə: 150, ɛ: 140, ɪ̞: 130, ʊ: 160, ɘ: 150, əl: 130, əɫ: 130, ɫ̩: 130,
      ɐ: 140, i: 140, ɪ: 140, ɜ: 220,
    },
    adds: englishLengthen,
  },
  rules: [
    ...nonRhoticRules,
    { from: 'θɪ̞ŋ', to: 'θɪŋk', next: ['#word'] },
    { from: 'hj', to: 'j', previous: ['#word'] },
    { from: 'h', to: '', previous: ['#word'], next: ['vowel'] },
  ],
  rules2: commonFinalRules,
};

export const scottish: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    'loch': 'ˈɫɒx', // or /ˈɫäx/?
  },
  lexicalSets: {
    FACE: 'eː', PRICE: 'ɜi̯', GOAT: 'oː', CHOICE: 'oi̯', MOUTH: 'ɜʉ̯',
    START: 'aə̯ɾ', SQUARE: 'eə̯ɾ', lettER: 'əɾ', NEAR: 'iə̯ɾ', NORTH: 'ɔɾ', FORCE: 'oə̯ɾ', CURE: 'ʉɾ', NURSE: 'ɪɾ',
    PALM: 'ä', LOT: 'ɔ', THOUGHT: 'ɔ', FLEECE: 'i', GOOSE: 'ʉ', TRAP: 'ä', BATH: 'ä',
    CLOTH: 'ɔ', commA: 'ə', DRESS: 'ɛ', happY: 'e', KIT: 'e', STRUT: 'ɐ', FOOT: 'ʉ',
  },
  phonemeLengths: {
    map: {
      ä: 220, ɐ: 160, eː: 190, e: 160, ɛ: 190, i: 160, ɪ: 130,
      oː: 190, ɔ: 200, ʉ: 160, ə: 130, əː: 220, əl: 130, əɫ: 130, ɫ̩: 130,
      aə̯: 250, eə̯: 220, iə̯: 220, oi̯: 210, oə̯: 230, ɜi̯: 210, ɜʉ̯: 200,
    },
    adds: englishLengthen,
  },
  rules: [
    { from: 't', to: 'ʔ', previous: ['vowel'], syllable: { current: 'unstressed' } },  // e.g. butter
    { from: 'ð', to: 'θ', next: ['#word'] },  // e.g. with
    { from: 'ðz', to: 'θs', next: ['#word'] },  // e.g. youths
    // epenthetic vowels
    { from: 'ɾɫ', to: 'rə̆ɫ' },
    { from: 'ɾm', to: 'rə̆m' },
    { from: 'ɾn', to: 'rə̆n' },
    { from: 'ɫm', to: 'ɫə̆m' },
  ],
  rules2: commonFinalRules,
};

export const glasgowVernacular: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    loch: 'ˈɫa̠x',
    head: 'hˈid', never: 'nˈɪ̈vʌ̈ɹ', // subset of DRESS
    stand: 'st̪ˈɔn',   // subset of TRAP
    after: 'ˈɛf.t̪ʌ̈ɹ', // subset of BATH
    off: 'ˈa̠f',       // subset of CLOTH
    stay: 'st̪ˈʌɪ̯',    // subset of FACE
    more: 'ˈmeɹ',     // subset of NORTH
    do: 'ˈde',        // subset of GOOSE
    prize: 'pɹˈae̯z',  // subset of PRICE
    birth: 'bˈʌ̈ɹf', berth: 'bˈɛɹf', horses: 'hˈoɹsʌ̈z',
  },
  lexicalSets: {
    FACE: 'e', PRICE: 'ʌi̯', GOAT: 'o', CHOICE: 'ɔe̯', MOUTH: 'ʉ',
    START: 'eɹ', SQUARE: 'eɹ', NEAR: 'iɹ', NORTH: 'oɹ', FORCE: 'oɹ',
    CURE: 'ʉɹ', lettER: 'ʌ̈ɹ', NURSE: 'ɪɹ',
    PALM: 'a̠', LOT: 'o', THOUGHT: 'o', CLOTH: 'o', TRAP: 'a̠', BATH: 'a̠',
    DRESS: 'ɛ', FLEECE: 'i̠', happY: 'e', KIT: 'i', GOOSE: 'ʉ', FOOT: 'ɪ',
    STRUT: 'ʌ̈', commA: 'ʌ̈',
  },
  phonemeLengths: {
    map: {
      a̠: 200, e: 180, ɛ: 140,
      i̠: 175, i: 160, ɪ: 150,
      o: 190, ʉ: 200, ʌ̈: 150,
      ʌi̯: 230, ɔe̯: 230, əː: 220, əl: 130, əɫ: 130, ɫ̩: 130,
      eɹ: 240, iɹ: 240, oɹ: 230, ʉɹ: 230, ʌ̈ɹ: 190, ɪɹ: 240,
      ɐ: 140, ə: 130,
    },
    adds: englishLengthen,
  },
  rules: [
    { from: 'hw', to: 'ʍ', previous: ['#word'] },
    { from: 'd', to: '', previous: { or: ['/ɫ/', '/n/'] }, next: ['#word'] },
    { from: 't͡ʃ', to: 't͡ʃ' },  // so a later rule doesn't change it
    { from: 't', to: 'ʔ', previous: { or: [':vowel', '/ɹ/'] }, next: ['vowel'], syllable: { current: 'unstressed' } },
    { from: 't', to: 't̪' },
    { from: 'θ', to: 'f' },
    { from: 'ð', to: 'ð', previous: ['#word'] },
    { from: 'ð', to: 'v' },
  ],
  rules2: [
    ...commonFinalRules,
    { from: 't̪', to: 't̪ʰ', previous: ['#word'], next: aspFollowing },
    { from: 't̪', to: 't̪ʰ', previous: { not: '/s/' }, next: aspFollowing, syllable: { current: 'stressed' } },
  ],
}

export const aussieGeneral: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  useRPDictionary: true,
  syllableBreaks: englishPhonotactics,
  wholeWords: {
    yoghurt: '/ˈjəʉ̯ɡət/', data: '/ˈdaːtə/',
    precedence: '/ˈpɹɪsədəns/', precedent: '/ˈpɹɪsədənt/',
    zebra: '/ˈzebɹə/', basil: '/ˈbæzəɫ/',
    cache: '/ˈkæɪ̯ʃ/', buoy: '/ˈboɪ̯/',
    congress: '/ˈkɔnɡɹes/', progress: '/ˈpɹəʉ̯ɡɹes/',
    maroon: '/məˈɹun/',
    soul: '/ˈsɔʊ̯ɫ/', // soul is a subset of GOAT that's pronounced differently
  },
  lexicalSets: {
    FACE: 'æi̯', PRICE: 'ɑ̟e̯', GOAT: 'ɐʉ̯', CHOICE: 'oi̯', MOUTH: 'æɔ̯', START: 'äːɹ',
    SQUARE: 'e̞ːɹ', lettER: 'əɹ', NEAR: 'iə̯ɹ', NORTH: 'oɹ', FORCE: 'oɹ', CURE: 'uə̯ɹ', NURSE: 'ɜːɹ',
    PALM: 'äː', LOT: 'ɒ', THOUGHT: 'oː', FLEECE: 'ɪi̯', GOOSE: 'ʉː', TRAP: 'æ', BATH: 'äː',
    CLOTH: 'ɒ', commA: 'ə', DRESS: 'e̞', happY: 'ɪi̯', KIT: 'ɪ', STRUT: 'ɶ', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      æi̯: 220, ɑ̟e̯: 260, ɐʉ̯: 220, oi̯: 230, æɔ̯: 230, äː: 220,
      e̞ː: 220, iə̯: 240, o: 190, uə̯: 230, ɜː: 220,
      ɒ: 150, oː: 230, ɪi̯: 200, ʉː: 200, aː: 220,
      æ: 175, ə: 150, e̞: 150, ɪ: 130, ɶ: 140, ʊ: 150,
      əl: 130, əɫ: 130, ɫ̩: 130,
    },
    adds: englishLengthen,
  },
  rules: [
    ...nonRhoticRules,
    // in case diphthongs are missed by the lexical set code
    { from: 'eɪ̯', to: 'æɪ̯' }, // face
    { from: 'aɪ̯', to: 'ɑ̟e̯' }, // price
    { from: 'aʊ̯', to: 'æɔ̯' }, // mouth
    { from: 'eə̯', to: 'e̞ː' }, // square
    //{ from: 'iə̯', to: 'iə̯' }, // near
    { from: 'ɔɪ̯', to: 'oɪ̯' }, // choice
    { from: 'əʊ̯', to: 'ɐʉ̯' }, // goat
  ],
  rules2: commonFinalRules,
};

export const southAfricanGeneral: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  lexicalSets: {
    FACE: 'æɪ̯', PRICE: 'äː', GOAT: 'œː', CHOICE: 'ɔɪ̯', MOUTH: 'äː',
    START: 'ɑɹ', SQUARE: 'eːɹ', lettER: 'əɹ', NEAR: 'ɪə̯ɹ', NORTH: 'ɔɹ', FORCE: 'ɔɹ', CURE: 'ɔɹ', NURSE: 'ø̈ːɹ',
    PALM: 'ɑː', LOT: 'ʌ̈', THOUGHT: 'oː', FLEECE: 'iː', GOOSE: 'ʉː', TRAP: 'a', BATH: 'ɑː',
    CLOTH: 'ʌ̈', commA: 'ə', DRESS: 'e', happY: 'iː', KIT: 'ɪ̈', STRUT: 'ɐ', FOOT: 'ɵ',
  },
  phonemeLengths: {
    map: {
      æɪ̯: 220, äː: 230, œː: 220, ɔɪ̯: 230,
      ɑ: 190, eː: 220, ə: 150, ɪə̯: 240, ɔ: 210, ø̈ː: 220,
      ɑː: 220, ʌ̈: 150, oː: 230, iː: 190, ʉː: 200, əː: 220,
      a: 175, e: 140, ɪ̈: 130, ɐ: 140, ɵ: 150,
    },
    adds: englishLengthen,
  },
  rules: nonRhoticRules,
  rules2: commonFinalRules,
};

// rhotic
export const southernUS: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  lexicalSets: {
    FACE: 'ɛi̯', PRICE: 'äː', GOAT: 'ɜʊ̯', CHOICE: 'oi̯', MOUTH: 'ɜʊ̯',
    START: 'ɒɹ', SQUARE: 'e̞ɹ', lettER: 'ɚ', NEAR: 'iɹ', NORTH: 'ɔɹ', FORCE: 'oɹ', CURE: 'uɹ', NURSE: 'ɐɹ',
    PALM: 'ɑ', LOT: 'ɑ', THOUGHT: 'ɑ', FLEECE: 'i', GOOSE: 'ɵu̯', TRAP: 'æɪ̯ə̆', BATH: 'æɪ̯ə̆',
    CLOTH: 'ɑ', commA: 'ə', DRESS: 'ɛɪ̯ə̆', happY: 'ɪ', KIT: 'ɪ', STRUT: 'ɜ', FOOT: 'ʏ',
  },
  phonemeLengths: {
    map: {
      ɑ: 200, i: 170, ɪ: 170, ɜ: 130, ʏ: 145, ə: 130, əː: 220,
      äː: 250, ɛi̯: 230, ɜʊ̯: 230, oi̯: 285,
      æɪ̯ə̆: 240, ɛɪ̯ə̆: 240, ɵu̯: 220, əl: 130, əɫ: 130, ɫ̩: 130,
      ɒɹ: 260, ɐɹ: 230, e̞ɹ: 280, iɹ: 280, ɪɹ: 260, ɔɹ: 250, oɹ: 250, uɹ: 250, ɚ: 210,
    },
    adds: englishLengthen,
  },
  rules: commonFinalRules,
};

// rhotic
export const shakespeare: RuleSet = {
  preprocess: ["cleanPunctuation", "lowerCase", "cleanLigatures"],
  wholeWords: {
    cure: 'ˈkçuɹ',
    // in TRAP rather than DRESS
    any: 'ˈanəɪ̯', many: 'ˈmanəɪ̯',
    // changed from GOOSE
    fool: 'ˈfʊɫ', conclude: 'kən.ˈkɫʊd', tooth: 'ˈtʊθ', proof: 'ˈpɹʊf',
    // in START rather than NORTH
    warm: 'ˈwaɹm', war: 'ˈwaɹ', quarter: 'ˈkaɹtɝ', warn: 'ˈwaɹn',
    // weak forms
    and: 'ən', as: 'əz', being: 'bɪn', for: 'fɚ', he: 'ˈə', I: 'ˈa',
    my: 'mɪ', mine: 'mɪn', thine: 'ðɪn', must: 'məs', of: 'ə', or: 'ə', them: 'ˈəm', thou: 'ˈðə',
    thee: 'ˈðɪ', thy: 'ðɪ', to: 'tə',
    be: 'bə', can: 'kən',
    window: 'ˈʍɪn.də',
    // h-dropping
    "he’s": 'əz', him: 'ɪm', his: 'ɪz', her: 'ɐɹ', hers: 'ɐɹz',
    // eliding of /v/ & /ð/
    heaven: 'ˈhɛə̆n', even: 'ˈiə̆n', seven: 'ˈsɛə̆m', eleven: 'əˈlɛə̆m', devil: 'ˈdiːə̆ɫ',
    hither: 'ˈhɪɚ', thither: 'ˈðɪɚ',
    // eliding of vowels
    unworthiest: 'ʌn.ˈwɜ˞.ðjəst', delivery: 'də.ˈlɪv.ɹəi', leavening: 'ˈlɛv.nɪn',
    venomous: 'ˈvɛn.məs', everybody: 'ˈɛv.ɹib.di',
    // /w/ > [ʍ]
    square: 'ˈskʍɛɹ', what: 'ˈʍət',
    // different stress pattern
    canonize: 'ka.ˈnɤ.ˌnəɪ̯z', advertize: 'ad.ˈvɛɹ.təɪ̯z', gallantry: 'ɡa.ˈɫan.tɹəɪ̯',
  },
  syllableBreaks: englishPhonotactics,
  lexicalSets: {
    FACE: 'ɛː', PRICE: 'əɪ̯', GOAT: 'oː', CHOICE: 'əɪ̯', MOUTH: 'əʊ̯',
    START: 'aɹ', SQUARE: 'ɛɹ', lettER: 'ɝ', NEAR: 'ɪɹ', NORTH: 'ɔɹ', FORCE: 'ɔɹ', CURE: 'uɹ', NURSE: 'ɐɹ',
    PALM: 'ɑ', LOT: 'ɑ', THOUGHT: 'ɑ', FLEECE: 'e̞', GOOSE: 'u', TRAP: 'a', BATH: 'a',
    CLOTH: 'ɔ', commA: 'ə', DRESS: 'ɛ', happY: 'əɪ̯', KIT: 'ɪ', STRUT: 'ɤ', FOOT: 'ʊ',
  },
  phonemeLengths: {
    map: {
      ɛː: 230, əɪ̯: 210, oː: 230, əʊ̯: 235,
      aɹ: 260, ɐɹ: 230, ɛɹ: 280, ɪɹ: 280, ɔɹ: 250, uɹ: 250,
      ɝ: 210, ɚ: 210, ɑ: 180, ɔ: 200, a: 210, ɛ: 195, e̞: 200,
      ɪ: 160, u: 190, ʊ: 145, ə: 130, ɤ: 130,
    },
    adds: englishLengthen,
  },
  rules: [
    // drop h at start of word or unstressed syllable
    { from: 'h', to: '', previous: ['#word'] },
    { from: 'h', to: '', syllable: { current: 'unstressed' } },
    // unstressed at end-of-word /ɪŋ/ > [ɪn]
    { from: 'ɪŋ', to: 'ɪn', next: ['#word'], syllable: { current: 'unstressed' } },
    // /w/ > [ʍ] at start of word
    { from: 'w', to: 'ʍ', previous: ['#word'] },
    // end-of word /ʃən/ > [sɪən]
    { from: 'ʃən', to: 'sɪən', next: ['#word'] },
    { from: 'ʃənz', to: 'sɪənz', next: ['#word'] },
    { from: 'ʒən', to: 'zɪən', next: ['#word'] }, { from: 'ʒɤn', to: 'zɪən', next: ['#word'] },
    { from: 'ʒənz', to: 'zɪənz', next: ['#word'] }, { from: 'ʒɤnz', to: 'zɪənz', next: ['#word'] },
  ],
  rules2: commonFinalRules,
};

export type EnglishAccentType =
  | 'Dictionary US'
  | 'Dictionary UK'
  | 'Western US'
  | 'English RP'
  | 'Cockney'
  | 'Scouse (Liverpool)'
  | 'Brummie (Birmingham)'
  | 'Estuary'
  | 'New Dublin'
  | 'Local Dublin'
  | 'Welsh (Cardiff)'
  | 'Scottish'
  | 'Glasgow'
  | 'Australian'
  | 'South African'
  | 'Southern US'
  | 'Shakespeare'
  ;
export const supportedEnglishAccents = [
  'Dictionary US',
  'Dictionary UK',
  'Western US',
  'English RP',
  'Cockney',
  'Estuary',
  'Scouse (Liverpool)',
  'Brummie (Birmingham)',
  'New Dublin',
  'Local Dublin',
  'Welsh (Cardiff)',
  'Scottish',
  'Glasgow',
  'Australian',
  'South African',
  'Southern US',
  'Shakespeare',
];

/** accent name with associated phoneme-modifying rules */
export const englishAccentRules: Record<string, RuleSet> = {
  'Dictionary US': dictionaryUS,
  'Dictionary UK': dictionaryUK,
  'Western US': westernUS,
  'English RP': receivedPronunciation,
  'Cockney': cockney,
  'Estuary': estuary,
  'Scouse (Liverpool)': liverpool,
  'Brummie (Birmingham)': brummie,
  'New Dublin': newDublin,
  'Local Dublin': localDublin,
  'Welsh (Cardiff)': welshCardiff,
  'Scottish': scottish,
  'Glasgow': glasgowVernacular,
  'Australian': aussieGeneral,
  'South African': southAfricanGeneral,
  'Southern US': southernUS,
  'Shakespeare': shakespeare,
}

/** true if a given accent is rhotic (pronounces the r's) */
export const rhoticAccent: Record<EnglishAccentType, boolean> = {
  'Dictionary US': true,
  'Dictionary UK': false,
  'Western US': true,
  'English RP': false,
  'Cockney': false,
  'Estuary': false,
  'Scouse (Liverpool)': false,
  'Brummie (Birmingham)': false,
  'New Dublin': true,
  'Local Dublin': true,
  'Welsh (Cardiff)': false,
  'Scottish': true,
  'Glasgow': true,
  'Australian': false,
  'South African': false,
  'Southern US': true,
  'Shakespeare': true,
}

export const accentLinks: Record<EnglishAccentType, string> = {
  'Dictionary US': '',
  'Dictionary UK': '',
  'Western US': "https://en.wikipedia.org/wiki/General_American_English",
  'English RP': "https://en.wikipedia.org/wiki/Received_Pronunciation",
  'Cockney': 'https://en.wikipedia.org/wiki/Cockney',
  'Estuary': 'https://en.wikipedia.org/wiki/Estuary_English',
  'Scouse (Liverpool)': "https://en.wikipedia.org/wiki/Scouse",
  'Brummie (Birmingham)': 'https://en.wikipedia.org/wiki/Brummie_dialect',
  'New Dublin': 'https://en.wikipedia.org/wiki/Dublin_English',
  'Local Dublin': 'https://en.wikipedia.org/wiki/Dublin_English',
  'Welsh (Cardiff)': 'https://en.wikipedia.org/wiki/Cardiff_English',
  'Scottish': 'https://en.wikipedia.org/wiki/Scottish_English',
  'Glasgow': "https://en.wikipedia.org/wiki/Glasgow_dialect",
  'Australian': "https://en.wikipedia.org/wiki/Australian_English_phonology",
  'South African': 'https://en.wikipedia.org/wiki/South_African_English',
  'Southern US': 'https://en.wikipedia.org/wiki/Southern_American_English',
  'Shakespeare': 'https://originalpronunciation.com/Files/PaulMeier_OP.pdf',
}
